
import * as utc from "../../util/utc.js";
import {resolveTimeCursor} from "../../util/dates.js";
import {newProduct, nav1hr, nav3hr} from "../productUtils.js";
import {tr} from "../../ux/translations.js";
import * as sources from "../sources.js";
import {scalarProduct} from "../scalarProduct.js";
import {isKioskContext} from "../../util/context.js";
import {ø} from "../../util/objects.js";
import {resolveValidTime} from "../productUtils.js";
import {createUnitDescriptors} from "../../util/units.js";
import {gaiaPath} from "../gaia.js";
import {createCO2Palette} from "../../palette/co2.js";
import {createCO2SCVariantAPalette, createCO2SCVariantBPalette} from "../../palette/co2sc.js";
import {createCOSCPalette} from "../../palette/cosc.js";
import {createSO2SmassPalette} from "../../palette/so2smass.js";
import {createDuexttauPalette} from "../../palette/duexttau.js";
import {createSuexttauPalette} from "../../palette/suexttau.js";


const aotLink = isKioskContext() ? "" : "(<a href='about.html#aot' class='internal-link'>AOT</a>)";

// This is the day when GAIA changed from processing GEOS-5 on 3-hourly steps to 1-hourly steps.
const TIMELINE_CHANGE_DATE = utc.date({year: 2019, month: 9, day: 1});
const GEOS_TIMELINE = [
    {
        type: "recurring_interval",
        start: "2015-07-25T00:00Z",
        end: "2019-09-01T00:00Z",
        frequency: {hours: 3},
    },
    {
        type: "recurring_interval",
        start: "2019-09-01T00:00Z",
        frequency: {hours: 1},
    },
];
const GEOS_CO2SC_TIMELINE = [
    {
        type: "recurring_interval",
        start: "2015-07-24T22:30Z",
        frequency: {hours: 3},
    },
];

let palettes = {};

/***
 * @param {object} attr
 * @param {string} type
 * @param {object?} alignment
 * @param {object?} offset
 * @returns {string}
 */
function geosPath(attr, type, alignment, offset) {
    // UNDONE: make layer.path a function and then use this.validTime() instead of computing it here
    const {time_cursor} = attr;
    const resolvedTimeCursor = resolveTimeCursor(time_cursor);
    if (alignment === undefined) {
        alignment = utc.date(resolvedTimeCursor) > TIMELINE_CHANGE_DATE ? {hour: 1} : {hour: 3};
    }
    const validTime = resolveValidTime(resolvedTimeCursor, alignment, offset);
    const dir = time_cursor === "now" ? "current" : utc.print(validTime, "{YYYY}/{MM}/{DD}");
    const stamp = time_cursor === "now" ? "current" : utc.print(validTime, "{hh}{mm}");
    const file = [stamp, type, "geos.epak"].join("-");
    return gaiaPath("data/geos", dir, file);
}

/**
 * @param {object} attr
 * @param {array} timeline
 */
function newGEOSProduct(attr, timeline) {
    // UNDONE: compute validTime here and pass into the nav1+nav3. also allows removal of offset.
    //         then co2sc can pass validTime directly into nav3
    const nav = utc.date(resolveTimeCursor(attr.time_cursor)) > TIMELINE_CHANGE_DATE ? nav1hr : nav3hr;
    return {
        sourceHTML: sources.geos,
        timeline,
        ...nav(attr.time_cursor),
    };
}

export function createCO2Layer(attr) {
    return ø(newProduct(), newGEOSProduct(attr, GEOS_TIMELINE), {
        type: "co2",
        descriptionHTML: {
            name: tr`Carbon Dioxide Mixing Ratio`,
            qualifier: "",
        },
        paths: [geosPath(attr, "co2")],
        builder: file => scalarProduct(file, /CO2CL/, {hasMissing: false}),
        unitDescriptors: createUnitDescriptors({  // CO2 Bulk Mixing Ratio (Column Mass/ps), units: mol/mol
         // "ppmv":     {convert: x => x * 1e6, precision: 1},
            "µmol/mol": {convert: x => x * 1e6, precision: 1},
        }),
        alpha: {single: 160, animated: 160},
        scale: palettes.co2 ??= createCO2Palette(),
    });
}

export function createCOSCLayer(attr) {
    return ø(newProduct(), newGEOSProduct(attr, GEOS_TIMELINE), {
        type: "cosc",
        descriptionHTML: {
            name: tr`Carbon Monoxide Surface Concentration`,
            qualifier: "",
        },
        paths: [geosPath(attr, "cosc")],
        builder: file => scalarProduct(file, /COSC/, {hasMissing: false}),
        unitDescriptors: createUnitDescriptors({  // CO Surface Concentration in ppbv, units: 1e-9
            "ppbv": {convert: x => x,        precision: 0},
            "ppmv": {convert: x => x / 1000, precision: 2},
        }),
        alpha: {single: 160, animated: 140},
        scale: palettes.cosc ??= createCOSCPalette(),
    });
}

export function createSO2SmassLayer(attr) {
    return ø(newProduct(), newGEOSProduct(attr, GEOS_TIMELINE), {
        type: "so2smass",
        descriptionHTML: {
            name: tr`Sulfur Dioxide Surface Mass`,
            qualifier: "",
        },
        paths: [geosPath(attr, "so2smass")],
        builder: file => scalarProduct(file, /SO2SMASS/, {hasMissing: false}),
        unitDescriptors: createUnitDescriptors({  // SO2 Surface Mass Concentration, units: kg/m^3
            "µg/m^3": {convert: x => x * 1e9,                precision: 2},
         // "ppb?":   {convert: x => x * 1e9 / 2.86,         precision: 0},
         // "ppm?":   {convert: x => x * 1000 * 1000 / 2.86, precision: 3},
         // "mg/m^3": {convert: x => x * 1000 * 1000,        precision: 3},
         // "kg/m^3": {convert: x => x,                      precision: 14},
        }),
        alpha: {single: 160, animated: 140},
        scale: palettes.so2smass ??= createSO2SmassPalette(),
    });
}

export function createDuexttauLayer(attr) {
    return ø(newProduct(), newGEOSProduct(attr, GEOS_TIMELINE), {
        type: "duexttau",
        descriptionHTML: {
            name: `${tr`Dust Extinction (Aerosol Optical Thickness, 550 nm)`} ${aotLink}`,
            qualifier: "",
        },
        paths: [geosPath(attr, "duexttau")],
        builder: file => scalarProduct(file, /DUEXTTAU/, {hasMissing: false}),
        unitDescriptors: createUnitDescriptors({  // Dust Extinction AOT (aerosol optical thickness) [550 nm], units: τ
            "τ": {convert: x => x, precision: 4},
        }),
        alpha: {single: 160, animated: 140},
        scale: palettes.duexttau ??= createDuexttauPalette(),
    });
}

export function createSuexttauLayer(attr) {
    return ø(newProduct(), newGEOSProduct(attr, GEOS_TIMELINE), {
        type: "suexttau",
        descriptionHTML: {
            name: `${tr`Sulfate Extinction (Aerosol Optical Thickness, 550 nm)`} ${aotLink}`,
            qualifier: "",
        },
        paths: [geosPath(attr, "suexttau")],
        builder: file => scalarProduct(file, /SUEXTTAU/, {hasMissing: false}),
        unitDescriptors: createUnitDescriptors({  // SO4 Extinction AOT (aerosol optical thickness) [550 nm], units: τ
            "τ": {convert: x => x, precision: 3},
        }),
        alpha: {single: 240, animated: 140},
        scale: palettes.suexttau ??= createSuexttauPalette(),
    });
}

export function createCO2scLayer(attr) {
    const alignment = {hour: 3};
    const offset = {minute: 90};  // CONSIDER: only reason offest is needed is because start date not provided...
    const validTime = resolveValidTime(resolveTimeCursor(attr.time_cursor), alignment, offset);
    const cutoff = utc.date({year: 2017, month: 1, day: 24, hour: 4, minute: 30});
    const adjustmentRequired = utc.date(validTime) < cutoff;  // old version of GEOS-5 needs CO2 offset
    const Δ = adjustmentRequired ? 32 : 0;
    const upgradeDate = utc.date({year: 2018, month: 7, day: 11, hour: 4, minute: 30});
    const variant = utc.date(validTime) < upgradeDate ? "A" : "B";

    function applyDelta(data) {
        for (let i = 0; i < data.length; i++) {
            data[i] += Δ;
        }
    }

    const palette =
        variant === "A" ?
            adjustmentRequired ?
                palettes.co2scAX ??= createCO2SCVariantAPalette(-8) :
                palettes.co2scAY ??= createCO2SCVariantAPalette(0) :
            palettes.co2scB ??= createCO2SCVariantBPalette();

    return ø(newProduct(), newGEOSProduct(attr, GEOS_CO2SC_TIMELINE), {
        type: "co2sc",
        descriptionHTML: {
            name: tr`Carbon Dioxide Surface Concentration`,
            qualifier: "",
        },
        sourceHTML: sources.geos,
        paths: [geosPath(attr, "co2sc", alignment, offset)],
        ...nav3hr(attr.time_cursor, offset),
        builder: file => scalarProduct(file, /CO2SC/, {hasMissing: false, transform: Δ !== 0 ? applyDelta : a => a}),
        unitDescriptors: createUnitDescriptors({  // CO2 Surface Concentration, units: ppmv
            "ppmv": {convert: x => x, precision: 0},
        }),
        alpha: {single: 200, animated: 150},
        scale: palette,
    });
}
